import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
} from '@mui/material';
import { forwardRef } from 'react';
import { Components, Virtuoso } from 'react-virtuoso';
import EmptyPlaceholder from '../../../../../../../../../components/Table/EmptyPlaceholder';
import LangEnum from '../../../../../../../../../lang/LangEnum';
import { EMPTY_CHAT_PATH } from '../../../../../../../../../utils/images.constants';
import { useApplicationContext } from '../../../../../../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../../../../../../translation/TranslationContext';
import { useChatContext } from '../../../../../../../contexts/chat/chat.context';
import { GroupedChatRoomsState } from '../../../../../../../contexts/chat/hooks/useChats/reducer/chatListReducer.types';
import { CHAT_FOOTER_HEIGHT } from '../../../utils/styles.const';
import ChatRoomItem from '../common/ChatRoomItem/ChatRoomItem';
import CreateNewChatFooter from '../common/CreateNewChatFooter/CreateNewChatFooter';
import GroupedRoomItem from './components/GroupedRoomItem/GroupedRoomItem';
import { GroupedRoomItemProps } from './components/GroupedRoomItem/groupedRoomItem.types';
import { useGroupedRoomsContentStyles } from './groupedRoomsContent.styles';

const CustomList: Components<GroupedChatRoomsState>['List'] = forwardRef(
  ({ children, style, ...props }, listRef) => (
    <List
      ref={listRef}
      component="div"
      {...props}
      style={{ ...style, margin: 0, overflow: 'hidden' }}
    >
      {children}
    </List>
  ),
);

const CustomItem: Components<GroupedChatRoomsState>['Item'] = ({ children, style, ...props }) => (
  <>
    <ListItem component="div" {...props} style={{ ...style, margin: 0, padding: 0 }}>
      {children}
    </ListItem>
    <Divider />
  </>
);

const GroupedRoomsContent = () => {
  const { translate } = useTranslation();
  const { isStore } = useApplicationContext();

  const { classes } = useGroupedRoomsContentStyles();

  const {
    rooms,
    loadMore,
    isLoading,
    goToChatListOfGroup,
    getCountOfChatsWithNotificationsByChatIds,
  } = useChatContext();

  const validateIfShouldRenderChatRoomItem = (room: GroupedChatRoomsState) => {
    const isGroupOfOneChat = room.chatIdsPresentInThisGrouping.length === 1;
    const hasLoadedChat = room.chats.length === 1 || !!room.mainChat;

    return isGroupOfOneChat && hasLoadedChat;
  };

  const mountGroupRoomItemProps = (room: GroupedChatRoomsState): GroupedRoomItemProps => {
    const notificationsCount = getCountOfChatsWithNotificationsByChatIds(
      room.chatIdsPresentInThisGrouping,
    );

    if (isStore) {
      return {
        groupImageSrc: room.brandInfo.logoUrl,
        groupName: room.brandInfo.commercialName,
        chatWithNotificationCount: notificationsCount,
        onClick: () => goToChatListOfGroup(room.metaId),
      };
    }

    return {
      groupImageSrc: room.storeInfo?.logoUrl,
      chatWithNotificationCount: notificationsCount,
      onClick: () => goToChatListOfGroup(room.metaId),
      groupName: room.storeInfo?.commercialName || '-', //TODO: NA TEORIA NUNCA CHEGARÁ NESSE '-' POIS PARA MARCA, QUANDO AGRUPAMENTO, SIGNIFICA QUE É UM CHAT PRIVADO, LOGO, TERÁ O storeInfo PREENCHIDO, É UM PROBLEMA MAIS DE TIPAGEM
    };
  };

  const showCircularProgress = !rooms.length && isLoading;
  const showLinearProgress = !!rooms.length && isLoading;

  return (
    <Box
      sx={{
        height: '100%',
      }}
    >
      {showCircularProgress && (
        <Box
          className={classes.listHeight}
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
      {rooms.length > 0 && (
        <Box className={classes.listHeight}>
          <Virtuoso
            data={rooms}
            endReached={loadMore}
            increaseViewportBy={{ bottom: 100, top: 100 }}
            components={{
              List: CustomList,
              Item: CustomItem,
            }}
            className={classes.scrollableContent}
            itemContent={(_, room) =>
              validateIfShouldRenderChatRoomItem(room) ? (
                <ChatRoomItem chat={room.chats[0] || room.mainChat} />
              ) : (
                <GroupedRoomItem {...mountGroupRoomItemProps(room)} />
              )
            }
          />
        </Box>
      )}
      {!rooms.length && !isLoading && (
        // TODO: ALTERAR PARA FORMATO QUE O HENRIQUE DESENHAR (AINDA NÃO HÁ)
        <Grid
          container
          style={{ marginBottom: '16px' }}
          sx={{
            justifyContent: 'center',
          }}
        >
          <Grid size={12}>
            <EmptyPlaceholder
              padding={1}
              height={150}
              width={150}
              src={EMPTY_CHAT_PATH}
              message={translate(LangEnum.CHAT_ROOM_USER_WITHOUT_CHAT)}
              typographyProps={{ variant: 'body1' }}
            />
          </Grid>
        </Grid>
      )}
      {showLinearProgress && <LinearProgress />}
      <Divider />
      <Box
        sx={{
          height: CHAT_FOOTER_HEIGHT,
        }}
      >
        <CreateNewChatFooter />
      </Box>
    </Box>
  );
};

export default GroupedRoomsContent;
